import { useContext, useEffect, useMemo, useState } from 'react';

import { setPhaseHash, setStorageValue } from 'App/Helpers';
import { ConsentContext, LanguageContext } from 'App/Providers/games';
import { LandingVideoProvider } from 'App/Providers/games/LandingVideoProvider';
import { useAnalytics } from 'App/Services/Analytics';

import lpThumbnail from '../../assets/lp-thumbnail.png';
import AgeConfirmationModal from '../../components/AgeModals/AgeConfirmationModal';
import AgeRestrictionPromoModal from '../../components/AgeModals/AgeRestrictionPromoModal';
import { STORAGE_KEY } from '../../configs';
import { AppContext } from '../../providers';

import Footer from './components/Footer';
import GameDescription from './components/GameDescription';
import HeroLightning from './components/HeroLightning';
import HowPlay from './components/HowPlay';
import WhyFun from './components/WhyFun';

/**
 * Landing page root component.
 * Builds the full landing flow: header, hero, how-to-play, why-fun,
 * game description, and footer. Pulls localization, videos, and legal
 * configuration from context providers, sets the initial URL hash, and
 * handles the main "Play" CTA to switch app phases (push / pwa / game).
 * Age confirmation and restriction promo modals are controlled here when
 * the user taps Play (see playButtonClickHandler).
 *
 * @param {object} props - Component props.
 * @param {string} props.ageConfirmationStatus - Stored age answer; "below18" shows the under-18 promo instead of the landing content.
 * @param {() => void} props.onMisclicked - Handler for the under-18 promo misclicked link.
 * @param {boolean} props.showPushPhase - If true, Play button switches to the Push phase.
 * @param {boolean} props.showPwaPhase - If true (and showPushPhase is false), Play button switches to the PWA phase.
 * @returns {import("react").JSX.Element} - The landing page markup with the age and legal modals.
 */
const Landing = ({ ageConfirmationStatus, onMisclicked, showPushPhase, showPwaPhase }) => {
    const [checked, setChecked] = useState(false);
    const [isAgeConfirmationOpen, setIsAgeConfirmationOpen] = useState(false);
    const [isAgeRestrictionPromoOpen, setIsAgeRestrictionPromoOpen] = useState(false);

    const {
        botInfo,
        lpVideos,
        urlParams,
        updatePhase,
        sharpstarService,
        appConfig: { useLocalLegalFiles, pushCampaignConfig },
    } = useContext(AppContext);
    const { sendGameDesignEvent, sendGoogleBasicEvent } = useAnalytics();
    const { locale, path } = useContext(LanguageContext);
    const { consentBannerReady } = useContext(ConsentContext);

    useEffect(() => {
        setPhaseHash('1');
    }, []);

    const moveToNextPhase = () => {
        updatePhase(showPushPhase ? 'push' : showPwaPhase ? 'pwa' : 'game');
        setStorageValue(STORAGE_KEY, 'app.ageConfirmation', 'above18');
    };

    const closeAgeConfirmationAndContinue = () => {
        setIsAgeConfirmationOpen(false);
        moveToNextPhase();
        sendGameDesignEvent('HP:AgeVerificationModal:True');
        sendGoogleBasicEvent('hp', 'age_verification_modal_true');
    };

    const openAgeRestrictionPromoModal = () => {
        setIsAgeConfirmationOpen(false);
        setIsAgeRestrictionPromoOpen(true);
        sendGameDesignEvent('HP:AgeVerificationModal:False');
        sendGoogleBasicEvent('hp', 'age_verification_modal_false');
        setStorageValue(STORAGE_KEY, 'app.ageConfirmation', 'below18');
    };

    const handleMisclicked = () => {
        setIsAgeRestrictionPromoOpen(false);
        onMisclicked();
    };

    const playButtonClickHandler = () => {
        sendGameDesignEvent('HP:PlayButton');
        sendGoogleBasicEvent('hp', 'play_button');

        if (!checked) {
            setIsAgeConfirmationOpen(true);
        } else {
            moveToNextPhase();
        }
    };

    const modalRid = useMemo(
        () => pushCampaignConfig[locale].modalRid,
        [locale, pushCampaignConfig]
    );

    return (
        <>
            {ageConfirmationStatus !== 'below18' && (
                <LandingVideoProvider videoUrl={lpVideos?.lp} posterUrl={lpThumbnail}>
                    <main>
                        <HeroLightning
                            lang={locale}
                            videoUrl={lpVideos?.lp}
                            consentBannerReady={consentBannerReady}
                            onBtnClick={playButtonClickHandler}
                            checked={checked}
                            setChecked={setChecked}
                        />

                        <HowPlay
                            lang={locale}
                            consentBannerReady={consentBannerReady}
                            onBtnClick={playButtonClickHandler}
                        />
                        <WhyFun
                            lang={locale}
                            videoUrl={lpVideos?.lp}
                            consentBannerReady={consentBannerReady}
                            onBtnClick={playButtonClickHandler}
                        />
                        <GameDescription lang={locale} />
                    </main>
                    <Footer path={path} lang={locale} useLocalLegalFiles={useLocalLegalFiles} />
                </LandingVideoProvider>
            )}

            {isAgeConfirmationOpen && (
                <AgeConfirmationModal
                    onYes={closeAgeConfirmationAndContinue}
                    onNo={openAgeRestrictionPromoModal}
                />
            )}

            {(isAgeRestrictionPromoOpen || ageConfirmationStatus === 'below18') && (
                <AgeRestrictionPromoModal
                    onMisclicked={handleMisclicked}
                    sharpstarService={sharpstarService}
                    urlParams={urlParams}
                    botInfo={botInfo}
                    rid={modalRid}
                />
            )}
        </>
    );
};

export default Landing;
